import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Divider, Grid, Typography } from '@mui/material'
import moment, { Moment } from '@e3dc-react/shell/Libs/moment'
import {
  addDays,
  addMonths,
  getDate,
  getMonth,
  getYear,
  isBefore,
  isAfter,
  isValid as isValidDate,
  isSameDay,
  startOfDay,
} from '@e3dc-react/shell/Libs/date'
import React, { useEffect, useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import { useShopCart } from '../../hooks'
import { DesiredShippingDate, ShopCheckoutSideBarResponse } from '../../models'
import { DatePicker } from '@e3dc-react/shell/Elements/InputFields'
import { getDisabledSystemConfigureShippingMonths, fetchCurrentAndNextYearHolidayDates, isDateWeekend, SystemConfigurationFixFlex } from '../../utils'
import { DateValidationError } from '@mui/x-date-pickers/internals'

const useStyles = makeStyles()(theme => ({
  container: { paddingLeft: theme.spacing(4), paddingRight: theme.spacing(4) },
  normalText: {
    color: theme.palette.text.primary,
    borderColor: theme.palette.grey.grey03,
  },
  datePicker: {
    marginTop: theme.spacing(4),
  },
}))

interface ConfirmCheckoutDesiredShippingDateProps {
  shippingDate: DesiredShippingDate | null
  maxShippingDateSelect: Date | null
  disabledDaysOffset?: number
  fixFlexMonths?: SystemConfigurationFixFlex[]
  onChange: (cartDetails: ShopCheckoutSideBarResponse | null) => void
  onHasError: (error: boolean) => void
}

const MONTH_OFFSET = 1

const formatShippingDate = (shippingDate: DesiredShippingDate | null | undefined): Date | null | undefined => {
  if (!shippingDate) {
    return null
  }

  const shippingDateDay = shippingDate.day ?? 0
  const shippingDateMonth = shippingDate.month ?? 0
  const shippingDateYear = shippingDate.year ?? 0
  return new Date(shippingDateYear, shippingDateMonth - MONTH_OFFSET, shippingDateDay)
}

/*
 * This component shows DatePicker for desired shipping date of the product
 */
const ConfirmCheckoutDesiredShippingDate: React.FC<ConfirmCheckoutDesiredShippingDateProps> = ({
  shippingDate,
  maxShippingDateSelect,
  disabledDaysOffset,
  fixFlexMonths,
  onChange,
  onHasError,
}) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const { setDesiredShippingDate } = useShopCart()
  const [desiredShippingDateError, setDesiredShippingDateError] = useState<string | null>(null)
  const [dateField, setDateField] = useState<Date | null | undefined>(formatShippingDate(shippingDate))
  const minimumDate = addDays(new Date(), disabledDaysOffset || 0)

  const onShippingDateChange = (date: Moment | null): void => {
    // Convert Moment from DatePicker to Date
    const dateAsDate = date ? date.toDate() : null

    if (!dateAsDate) {
      return onChange(null)
    }

    const isDateBeforeMinimumDate = isBefore(startOfDay(dateAsDate), startOfDay(minimumDate))
    const isDateAfterMaximumDate = isAfter(startOfDay(dateAsDate), startOfDay(maxShippingDateSelectabe))
    const isInvalidShippingDate = isMonthDisabled(dateAsDate) || !isValidDate(dateAsDate) || isDateBeforeMinimumDate || isDateAfterMaximumDate

    if (isInvalidShippingDate) {
      return setDateField(dateAsDate)
    }

    const day = getDate(dateAsDate)
    const month = getMonth(dateAsDate) + MONTH_OFFSET
    const year = getYear(dateAsDate)
    setDesiredShippingDate(day, month, year).then(result => {
      if (result?.extensions?.e3dcDesiredShippingDate) {
        const formattedDate = formatShippingDate(result.extensions.e3dcDesiredShippingDate)
        setDateField(formattedDate)
        onChange(result)
      }
    })
  }

  useEffect(() => {
    const formattedShippingDate = formatShippingDate(shippingDate)
    setDateField(formattedShippingDate)
    if (formattedShippingDate && isMonthDisabled(formattedShippingDate)) {
      onShippingDateError('shouldDisableDate')
    }
  }, [shippingDate]) //eslint-disable-line

  /* since date-fns starts counting from 1 i have added 5 months below so we can get the next 6 months in total */
  const sixMonthsFromNow = addMonths(new Date(), 5)
  const maxShippingDateSelectabe = maxShippingDateSelect ? maxShippingDateSelect : sixMonthsFromNow
  const filteredDisabledMonths = getDisabledSystemConfigureShippingMonths(fixFlexMonths)
  const availableHolidays = fetchCurrentAndNextYearHolidayDates()

  const onShippingDateError = (error: DateValidationError | null): void => {
    const hasError = Boolean(error)
    onHasError(hasError)
    if (error === 'invalidDate') return setDesiredShippingDateError(t('shop.confirm-checkout.desired-shipping-date-invalid-date'))
    if (error === 'shouldDisableDate') return setDesiredShippingDateError(t('shop.confirm-checkout.desired-shipping-date-should-disable-date'))
    if (error === 'disableFuture') return setDesiredShippingDateError(t('shop.confirm-checkout.desired-shipping-date-disable-future'))
    if (error === 'disablePast') return setDesiredShippingDateError(t('shop.confirm-checkout.desired-shipping-date-disable-past'))
    if (error === 'minDate') return setDesiredShippingDateError(t('shop.confirm-checkout.desired-shipping-date-min-date'))
    if (error === 'maxDate') return setDesiredShippingDateError(t('shop.confirm-checkout.desired-shipping-date-max-date'))
    setDesiredShippingDateError(null)
  }

  const isMonthDisabled = (date: Date): boolean => {
    /* since date-fns counts from 1 and the data comes from 0 i have added +1 month */
    const month = getMonth(date) + MONTH_OFFSET
    const year = getYear(date)

    if (isDateWeekend(date)) {
      return true
    }

    if (availableHolidays.some(holiday => isSameDay(holiday, date))) {
      return true
    }

    const hasFixFlexMonths = Boolean(fixFlexMonths?.length)

    if (hasFixFlexMonths) {
      const existsInFixFlexMonths = fixFlexMonths?.some(item => {
        return Boolean(item.fixFlex?.[year]?.[month])
      })

      if (!existsInFixFlexMonths) {
        return true
      }
    }

    return filteredDisabledMonths.some(disabledMonth => {
      return disabledMonth.month === month && disabledMonth.year === year
    })
  }

  return (
    <>
      <Grid item xs={12} md={6} className={classes.container}>
        <Typography variant="h2" sx={{ fontWeight: 400 }} className={classes.normalText}>
          {t('shop.confirm-checkout.desired-shipping-date')}
        </Typography>
        <Divider className={classes.normalText} variant="fullWidth" />
        <Box className={classes.datePicker}>
          <DatePicker
            label={t('shop.confirm-checkout.shipping-date-label')}
            format={t('common.format.date')}
            required
            helperText={desiredShippingDateError ?? undefined}
            minDate={moment(minimumDate)}
            maxDate={moment(maxShippingDateSelectabe)}
            value={dateField ? moment(dateField) : null}
            onChange={date => onShippingDateChange(date)}
            iconPaddingRight={'16px'}
            shouldDisableDate={(date: Moment) => isMonthDisabled(date.toDate())}
            variant={'outlined'}
            onError={error => onShippingDateError(error)}
          />
        </Box>
      </Grid>
      <Grid item xs={12} md={6} />
    </>
  )
}

export default ConfirmCheckoutDesiredShippingDate
